import { i18n } from './index';
import { debounce } from 'lodash';

// Cache for parsed translation paths
const pathCache = new Map();
const loadedTranslations = new Map();
const componentReferences = new Map();
const context = require.context('@/locales', true, /\.json$/);

// Add supported languages configuration
const SUPPORTED_LOCALES = ['en', 'vi', 'id', 'hi', 'ta']; // Add all supported languages here

// Configurable options
const CONFIG = {
  retryAttempts: 3,
  retryDelay: 1000,
  localeChangeDebounceMs: 250
};

class TranslationError extends Error {
  constructor(code, message) {
    super(message);
    this.code = code;
    this.name = 'TranslationError';
  }
}

function getLogger(component) {
  const componentId = component?._uid || 'unknown';
  return {
    warn: (msg, ...args) => console.warn(`[i18n:${componentId}] ${msg}`, ...args),
    error: (msg, ...args) => console.error(`[i18n:${componentId}] ${msg}`, ...args)
  };
}

function getTranslationPath(component, componentName) {
  const cacheKey = componentName || `${component?.$options?.__file}`;
  
  if (pathCache.has(cacheKey)) {
    return pathCache.get(cacheKey);
  }

  if(componentName){
    pathCache.set(componentName, componentName);
    return componentName
  }

  if (!component?.$options?.__file) {
    return null;
  }

  const path = component.$options.__file
    .replace(/^.*\/src\//, '')
    .replace(/\.vue$/, '')
    .split('/');

  const isComponent = path.includes('components');
  const isView = path.includes('views');

  if (!isComponent && !isView) {
    pathCache.set(cacheKey, null);
    return null;
  }

  const folder = isComponent ? 'components' : 'views';
  const result = componentName 
    ? `${folder}/${componentName}`
    : `${folder}/${path[path.length - 1]}`;

  pathCache.set(cacheKey, result);
  return result;
}

async function loadTranslationsWithRetry(locale, path, retryCount = 0) {
  try {
    const module = context(`./${locale}/${path}.json`);
    return module.default || module;
  } catch (error) {
    if (retryCount < CONFIG.retryAttempts) {
      await new Promise(resolve => setTimeout(resolve, CONFIG.retryDelay));
      return loadTranslationsWithRetry(locale, path, retryCount + 1);
    }
    throw new TranslationError('LOAD_FAILED', `Failed to load translations for ${path} after ${CONFIG.retryAttempts} attempts`);
  }
}

async function loadAllTranslations(component, i18nInstance, componentName) {
  const logger = getLogger(component);
  const path = getTranslationPath(component, componentName);
  
  if (!path) return;

  if (!componentReferences.has(path)) {
    componentReferences.set(path, new Set());
  }
  componentReferences.get(path).add(component._uid);

  // Load translations for all supported locales
  for (const locale of SUPPORTED_LOCALES) {
    try {
      const messages = await loadTranslationsWithRetry(locale, path);
      
      if (messages) {
        const currentMessages = i18nInstance.getLocaleMessage(locale)[path];
        if (!currentMessages || JSON.stringify(currentMessages) !== JSON.stringify(messages)) {
          i18nInstance.mergeLocaleMessage(locale, { [path]: messages });
          
          // Store translations in our cache for each locale
          if (!loadedTranslations.has(locale)) {
            loadedTranslations.set(locale, new Map());
          }
          loadedTranslations.get(locale).set(path, messages);
        }
      }
    } catch (error) {
      logger.error(`Translation loading error for locale ${locale}:`, error);
      
      // If primary locale fails, try loading English as fallback
      if (locale === i18nInstance.locale) {
        try {
          logger.warn(`Falling back to English translations for ${path}`);
          const fallbackMessages = await loadTranslationsWithRetry('en', path);
          if (fallbackMessages) {
            i18nInstance.mergeLocaleMessage(locale, { [path]: fallbackMessages });
            loadedTranslations.get(locale).set(path, fallbackMessages);
          }
        } catch (fallbackError) {
          logger.error('Fallback translation loading error:', fallbackError);
        }
      }
    }
  }
}

function unloadAllTranslations(component, i18nInstance, componentName) {
  const path = getTranslationPath(component, componentName);
  if (!path) return;

  const componentRefs = componentReferences.get(path);
  if (!componentRefs) return;

  componentRefs.delete(component._uid);

  if (componentRefs.size === 0) {
    componentReferences.delete(path);
    
    // Unload translations for all locales
    for (const locale of SUPPORTED_LOCALES) {
      if (loadedTranslations.has(locale) && loadedTranslations.get(locale).has(path)) {
        const currentMessages = i18nInstance.getLocaleMessage(locale);
        delete currentMessages[path];
        i18nInstance.setLocaleMessage(locale, currentMessages);
        loadedTranslations.get(locale).delete(path);
        
        // Clean up locale map if empty
        if (loadedTranslations.get(locale).size === 0) {
          loadedTranslations.delete(locale);
        }
      }
    }
  } else {
    // If there are remaining components, ensure translations stay loaded
    const remainingComponent = Array.from(componentRefs)[0];
    if (remainingComponent) {
      loadAllTranslations({ _uid: remainingComponent }, i18nInstance, componentName);
    }
  }
}

export function i18nComponentMixin(componentName = null) {
  return {
    beforeMount() {
      loadAllTranslations(this, i18n, componentName);
    },
    beforeDestroy() {
      unloadAllTranslations(this, i18n, componentName);
    },
    methods: {
      t(key, variables = {}) {
        const path = getTranslationPath(this, componentName);
        const translationKey = path ? `${path}.${key}` : key;

        const originalWarn = console.warn;
        console.warn = () => {}; 
      
        let translation;
        try {
          translation = this.$t(translationKey, variables);
        } finally {
          console.warn = originalWarn; 
        }
        // Ensure a valid string is returned
        return typeof translation === 'string' && !translation.includes(path) ? translation : key.split('.').pop().replace(/_/g, ' ').replace(/\s(.)/g, function($1) { return $1.toUpperCase(); }).replace(/^./, function($1) { return $1.toUpperCase(); });
      },

      d(date, format = 'long', locale = this.$i18n.locale) {
        const path = getTranslationPath(this, componentName);
        if (!date) return '';
        
        const dateFormats = this.$i18n.messages[locale]?.[path]?.dateFormats?.[format];
        const defaultFormat = { year: 'numeric', month: 'long', day: 'numeric' };
        
        return new Intl.DateTimeFormat(
          locale, 
          dateFormats || defaultFormat
        ).format(new Date(date));
      }
    }
  };
}